import { useState } from "react";

const FORM_ADDR = "https://readdy.ai/api/form/db53v8puu2cnvnf27gu0";

const genreOptions = ["爱情 / 甜宠", "悬疑 / 犯罪", "复仇 / 爽剧", "治愈 / 生活", "古装 / 奇幻", "青春 / 校园"];

export default function NewsletterSection() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString()
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const successCode = parsed?.code === "OK";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (!response.ok || !successCode || isSpam) {
        setStatus("error");
        setFormError(String(serverMsg) || "提交失败，请稍后重试。");
        return;
      }
      setStatus("success");
      setFormError("");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <section id="subscribe" className="w-full bg-background-100 py-16 md:py-20">
      <div className="mx-auto w-full max-w-5xl px-4 md:px-6">
        <div className="overflow-hidden rounded-lg border border-background-200/70 bg-background-50">
          <div className="grid grid-cols-1 lg:grid-cols-5">
            <div className="bg-secondary-500 p-6 md:p-8 lg:col-span-2">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-background-50/20 text-background-50">
                <i className="ri-notification-3-line text-xl" aria-hidden="true"></i>
              </span>
              <h2 className="mt-5 font-heading text-xl font-bold text-background-50 md:text-2xl">
                订阅追更提醒
              </h2>
              <p className="mt-3 font-body text-sm leading-relaxed text-background-100">
                留下邮箱，重要剧集更新或新剧开播时，我们会发一封简短提醒，告诉你更新了哪部、放到了第几集。
                只发追剧相关的内容，随时可取消。
              </p>
              <ul className="mt-6 space-y-2.5">
                {["更新与开播提醒", "每周精选剧单", "不发送无关广告"].map((t) => (
                  <li key={t} className="flex items-center gap-2 font-body text-sm text-background-100">
                    <i className="ri-check-line text-accent-300" aria-hidden="true"></i>
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-6 md:p-8 lg:col-span-3">
              <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div>
                  <label htmlFor="sub-email" className="font-label text-sm font-medium text-foreground-800">
                    邮箱地址
                  </label>
                  <input
                    id="sub-email"
                    type="email"
                    name="email"
                    required
                    placeholder="you@example.com"
                    className="mt-2 w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 font-body text-sm text-foreground-950 outline-none transition placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
                  />
                </div>

                <div>
                  <label htmlFor="sub-genre" className="font-label text-sm font-medium text-foreground-800">
                    最常看的题材（选填）
                  </label>
                  <select
                    id="sub-genre"
                    name="preferred_genre"
                    defaultValue=""
                    className="mt-2 w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 font-body text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
                  >
                    <option value="">请选择你喜欢的题材</option>
                    {genreOptions.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="drama-hp-guard" aria-hidden="true">
                  <label htmlFor="sub-website-alt">请勿填写此栏</label>
                  <input
                    id="sub-website-alt"
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                    aria-hidden="true"
                  />
                </div>

                <button
                  type="submit"
                  className="mt-1 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-send-plane-line" aria-hidden="true"></i>
                  免费订阅追更提醒
                </button>

                {status === "success" && (
                  <p
                    role="status"
                    className="flex items-center gap-2 rounded-md bg-secondary-100 px-4 py-2.5 font-body text-sm text-secondary-900"
                  >
                    <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                    订阅成功！我们会在下次剧集更新时通知你。
                  </p>
                )}
                {status === "error" && (
                  <p
                    role="alert"
                    className="flex items-center gap-2 rounded-md bg-primary-100 px-4 py-2.5 font-body text-sm text-primary-800"
                  >
                    <i className="ri-error-warning-line" aria-hidden="true"></i>
                    {formError || "提交失败，请稍后重试。"}
                  </p>
                )}
                <p className="font-body text-xs leading-relaxed text-foreground-500">
                  提交即表示你同意接收追剧提醒邮件。我们不会向第三方分享你的邮箱。
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}